/**
 * Tooltip (Phase 26)
 *
 * Tooltip Tailwind pur, accessible. Apparait au hover et au focus
 * (clavier OK), masque par defaut.
 *
 * Usage :
 *   <Tooltip label="Rafraichir"><button>...</button></Tooltip>
 *
 * Le tooltip est rendu en absolute positionning sur le parent group, donc
 * il faut wrapper dans un span avec relative + group.
 *
 * Pour les actions standardisees, utiliser <IconButton> qui inclut deja
 * le wrapper + le tooltip.
 */

import { type ReactNode } from 'react';

type Side = 'top' | 'bottom' | 'left' | 'right';

const SIDE_CLASSES: Record<Side, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const ARROW_CLASSES: Record<Side, string> = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-ink-700',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-ink-700',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-ink-700',
  right: 'right-full top-1/2 -translate-y-1/2 border-r-ink-700',
};

export type TooltipProps = {
  label: string;
  side?: Side;
  /** Ne pas afficher le tooltip (ex: sur mobile, ou si deja affiche en label) */
  disabled?: boolean;
  children: ReactNode;
};

export function Tooltip({ label, side = 'bottom', disabled, children }: TooltipProps) {
  if (disabled) return <>{children}</>;

  return (
    <span className="relative inline-flex group">
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-50 whitespace-nowrap px-2 py-1 text-2xs font-mono uppercase tracking-widest text-steel-100 bg-ink-900 border border-ink-700 shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity ${SIDE_CLASSES[side]}`}
      >
        {label}
        <span
          aria-hidden
          className={`absolute w-0 h-0 border-4 border-transparent ${ARROW_CLASSES[side]}`}
        />
      </span>
    </span>
  );
}
